'use client';

import { useMemo } from 'react';
import { APOLLO_ERROR_POLICY, APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { DocumentImageSize } from '@/publicSearch/constants';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useGetSocietiesQuery } from '@/publicSearch/graphql/queries.generated';
import { pickPrimaryImageUrl } from '@/publicSearch/utils/pickPrimaryImageUrl';
import { useImageFromEntity } from '../useImageFromEntity';
import { useAnimal } from './useAnimal';

export function useAnimalView(animalId: string, orgId: string) {
  const {
    data,
    loading,
    error: animalError,
    notFound,
  } = useAnimal(animalId, orgId);
  const animalData = data?.animal;

  // Get society from animal data, or query it separately if needed
  const societyId = animalData?.societyId || (orgId ? Number(orgId) : null);
  const { data: orgData, loading: loadingOrg } = useGetSocietiesQuery({
    skip: !societyId || isNaN(Number(societyId)),
    variables: {
      currentPage: 1,
      pageSize: 1,
    },
    context: {
      headers: setDefaultHeader(orgId),
    },
    errorPolicy: APOLLO_ERROR_POLICY.ALL,
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
  });

  const isLoading = loading || loadingOrg;
  const isLoadingImg = isLoading;
  const animalImgUrls = useMemo(() => {
    const docs = animalData?.documents ?? [];
    const primaryUrl = pickPrimaryImageUrl(docs);
    return primaryUrl ? [primaryUrl] : [];
  }, [animalData?.documents]);

  // Get organization from animal data or query
  const currentOrg = orgData?.societies.items[0];
  const orgEntityId = currentOrg?.societyId ? String(currentOrg.societyId) : '';

  const orgImgResult = useImageFromEntity(
    orgEntityId ?? '',
    orgId,
    DocumentImageSize.xl,
  );
  const orgImgUrl = orgImgResult.imgUrl;

  return {
    animalData,
    animalIdent: animalData?.animalIdent ?? '',
    animalImgUrls,
    animalStatus: animalData?.animalStatus ?? '',
    animalName: animalData?.animalName ?? '',
    animalDisplayName: animalData?.animalName, // Using animalName as display name
    breeder: animalData?.breeder
      ? {
          additionalInfo: {
            id: animalData.breeder.memberId,
            ident: animalData.breeder.memberIdent || '',
            name: animalData.breeder.memberName || '',
            detailsAvailFlag: animalData.breeder.detailsAvailFlag ?? null,
          },
        }
      : undefined,
    currentOrg: currentOrg
      ? {
          organisationId: currentOrg.societyId,
          name: currentOrg.name,
          description: '',
        }
      : undefined,
    // Do not OR in errorOrg: GetSocieties uses errorPolicy 'all' and can stay "errored" for
    // non-fatal GraphQL issues while the animal row is fine — that produced spurious
    // "Error resolving animal" toasts when combined with a brief !animalData window.
    error: animalError,
    owner: animalData?.currentOwner
      ? {
          additionalInfo: {
            id: animalData.currentOwner.memberId,
            ident: animalData.currentOwner.memberIdent || '',
            name: animalData.currentOwner.memberName || '',
            detailsAvailFlag: animalData.currentOwner.detailsAvailFlag ?? null,
          },
        }
      : undefined,
    isLoading,
    isLoadingImg,
    notFound,
    orgImgUrl,
  };
}
